// src/components/ConfiguracoesProjeto.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import JSZip from "jszip";
import "./ConfiguracoesProjeto.css";

const TIPOS_ORDEM = ["Atas", "Tarefas", "Lista", "Metas", "Diário de Obra"];

const TIPO_CLASS = {
  "Atas":           "cfg-badge cfg-badge--atas",
  "Lista":          "cfg-badge cfg-badge--lista",
  "Tarefas":        "cfg-badge cfg-badge--tarefas",
  "Diário de Obra": "cfg-badge cfg-badge--rdo",
  "Diário de obra": "cfg-badge cfg-badge--rdo",
  "Metas":          "cfg-badge cfg-badge--metas",
  "Nota Rápida":    "cfg-badge cfg-badge--rapida",
};

const TIPO_LABEL = {
  "Atas":           "Atas",
  "Tarefas":        "Tarefas",
  "Lista":          "Listas",
  "Metas":          "Metas",
  "Diário de Obra": "Diários",
  "Nota Rápida":    "Notas Rápidas",
};

const TIPO_ICON = {
  "Atas":           "description",
  "Tarefas":        "task_alt",
  "Lista":          "checklist",
  "Metas":          "flag",
  "Diário de Obra": "construction",
  "Nota Rápida":    "sticky_note_2",
};

const normalizarTipo = (t) => t === "Diário de obra" ? "Diário de Obra" : t;

const formatarData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
};

const sanitizarNomeArquivo = (nome) =>
  (nome || "ata").replace(/[/\\:*?"<>|]/g, "_").replace(/\s+/g, "_").slice(0, 80);

export default function ConfiguracoesProjeto({ entity, entityType, usuarioId }) {
  // ── States adicionados para status e deleção ──────────────────────────────
  const [ativo, setAtivo] = useState(entity?.ativo !== false);
  const [alterandoStatus, setAlterandoStatus] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteOption, setDeleteOption] = useState(null); // 'download' | 'direto'
  const [confirmText, setConfirmText] = useState("");
  const [deletando, setDeletando] = useState(false);
  // ────────────────────────────────────────────────────────────────────────────

  const [notas, setNotas]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [exportando, setExportando]   = useState(false);
  const [exportandoAtas, setExportandoAtas]     = useState(false);
  const [progressoAtas, setProgressoAtas]       = useState("");
  const [exportandoListas, setExportandoListas]   = useState(false);
  const [progressoListas, setProgressoListas]     = useState("");
  const [exportandoTarefas, setExportandoTarefas]   = useState(false);
  const [progressoTarefas, setProgressoTarefas]     = useState("");
  const [exportandoDiarios, setExportandoDiarios]   = useState(false);
  const [progressoDiarios, setProgressoDiarios]     = useState("");
  const [filtroTipo, setFiltroTipo]   = useState(null);

  useEffect(() => {
    if (!entity?.id) return;
    carregarTodasNotas();
  }, [entity?.id]);

  // ── Efeito para sincronizar o status ativo ────────────────────────────────
  useEffect(() => {
    setAtivo(entity?.ativo !== false);
  }, [entity?.id, entity?.ativo]);

  const carregarTodasNotas = async () => {
    setLoading(true);
    try {
      const idField = entityType === "project" ? "project_id" : "setor_id";

      const { data: pilhas } = await supabase
        .from("pilhas")
        .select("id, title, arquivada")
        .eq(idField, entity.id)
        .or("tipo_pilha.is.null,tipo_pilha.neq.sistema_calendario");

      if (!pilhas?.length) { setNotas([]); setLoading(false); return; }

      const pilhaIds = pilhas.map(p => p.id);
      const pilhaMap = Object.fromEntries(pilhas.map(p => [p.id, p]));

      const { data: notasRaw } = await supabase
        .from("notas")
        .select("id, nome, tipo, created_at, created_by, pilha_id")
        .in("pilha_id", pilhaIds)
        .order("created_at", { ascending: false });

      if (!notasRaw?.length) { setNotas([]); setLoading(false); return; }

      const notaIds = notasRaw.map(n => n.id);

      const creatorIds = [...new Set(notasRaw.map(n => n.created_by).filter(Boolean))];
      let perfilMap = {};
      if (creatorIds.length > 0) {
        const { data: perfis } = await supabase
          .from("profiles").select("id, nickname, nome").in("id", creatorIds);
        perfilMap = Object.fromEntries((perfis || []).map(p => [p.id, p.nickname || p.nome || "—"]));
      }

      const [
        { data: comentarios },
        { data: anexos },
        { data: objetivos },
      ] = await Promise.all([
        supabase.from("comentarios").select("nota_id").in("nota_id", notaIds),
        supabase.from("anexos").select("nota_id, mime_type").in("nota_id", notaIds),
        supabase.from("objetivos").select("nota_id").in("nota_id", notaIds),
      ]);

      const metaMap = {};
      notaIds.forEach(id => {
        metaMap[id] = {
          comentarios: (comentarios || []).filter(c => c.nota_id === id).length,
          anexos:      (anexos || []).filter(a => a.nota_id === id && !a.mime_type?.startsWith("image/")).length,
          imagens:     (anexos || []).filter(a => a.nota_id === id &&  a.mime_type?.startsWith("image/")).length,
          objetivos:   (objetivos || []).filter(o => o.nota_id === id).length,
        };
      });

      setNotas(notasRaw.map(n => ({
        ...n,
        tipo:           normalizarTipo(n.tipo),
        pilhaTitulo:    pilhaMap[n.pilha_id]?.title || "—",
        pilhaArquivada: pilhaMap[n.pilha_id]?.arquivada || false,
        criadoPor:      n.created_by ? (perfilMap[n.created_by] || "—") : "—",
        meta:           metaMap[n.id] || { comentarios: 0, anexos: 0, imagens: 0, objetivos: 0 },
      })));
    } catch (err) {
      console.error("Erro ao carregar notas:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Função para alternar status ativo/inativo ─────────────────────────────
  const toggleAtivo = async () => {
    const novoStatus = !ativo;
    const confirmMsg = novoStatus
      ? "Reativar este projeto? Ele voltará a aparecer normalmente."
      : "Inativar este projeto? Ele ficará marcado como inativo e com filtro cinza no container.";
    if (!window.confirm(confirmMsg)) return;

    setAlterandoStatus(true);
    try {
      const tabela = entityType === "project" ? "projects" : "setores";
      const { error } = await supabase
        .from(tabela)
        .update({ ativo: novoStatus })
        .eq("id", entity.id);
      if (error) throw error;
      setAtivo(novoStatus);
    } catch (err) {
      console.error("Erro ao alterar status:", err);
      alert(`Erro ao alterar status: ${err.message}`);
    } finally {
      setAlterandoStatus(false);
    }
  };

  // ── Função para executar todos os exports ─────────────────────────────────
  const executarTodosExports = async () => {
    if (resumo["Atas"]) await exportarAtasZip();
    if (resumo["Lista"]) await exportarListasZip();
    if (resumo["Tarefas"]) await exportarTarefasZip();
    if (resumo["Diário de Obra"]) await exportarDiariosZip();
  };

  // ── Função para confirmar deleção ─────────────────────────────────────────
  const confirmarDelecao = async () => {
    if (confirmText !== entity?.name) {
      alert("Digite o nome do projeto exatamente igual para confirmar.");
      return;
    }

    setDeletando(true);
    try {
      if (deleteOption === "download") {
        await executarTodosExports();
      }

      const tabela = entityType === "project" ? "projects" : "setores";
      const { error } = await supabase.from(tabela).delete().eq("id", entity.id);
      if (error) throw error;

      alert("Projeto apagado com sucesso.");
      window.location.href = "/"; // ajuste para a rota correta de listagem
    } catch (err) {
      console.error("Erro ao apagar projeto:", err);
      alert(`Erro ao apagar projeto: ${err.message}`);
    } finally {
      setDeletando(false);
      setShowDeleteModal(false);
    }
  };

  // ── Resumo por tipo ────────────────────────────────────────────────────────
  const resumo = notas.reduce((acc, n) => {
    acc[n.tipo] = (acc[n.tipo] || 0) + 1;
    return acc;
  }, {});

  const tiposPresentes = TIPOS_ORDEM.filter(t => resumo[t] > 0);
  const notasFiltradas = filtroTipo ? notas.filter(n => n.tipo === filtroTipo) : notas;

  // ── Export CSV da lista ────────────────────────────────────────────────────
  const exportarListaCSV = () => {
    setExportando(true);
    try {
      const linhas = [
        ["Nome", "Tipo", "Pilha", "Arquivada", "Data de Criação", "Criado por", "Comentários", "Anexos", "Imagens", "Objetivos"],
        ...notasFiltradas.map(n => [
          `"${(n.nome || "").replace(/"/g, '""')}"`,
          `"${n.tipo || ""}"`,
          `"${(n.pilhaTitulo || "").replace(/"/g, '""')}"`,
          n.pilhaArquivada ? "Sim" : "Não",
          formatarData(n.created_at),
          `"${(n.criadoPor || "").replace(/"/g, '""')}"`,
          n.meta.comentarios, n.meta.anexos, n.meta.imagens, n.meta.objetivos,
        ]),
      ];
      const blob = new Blob(["﻿" + linhas.map(l => l.join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `notas_${entity?.name || "projeto"}_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
    finally { setExportando(false); }
  };

  // ── Export ZIP de todas as atas ────────────────────────────────────────────
  const exportarAtasZip = async () => {
    setExportandoAtas(true);
    setProgressoAtas("Buscando atas…");
    try {
      const idField = entityType === "project" ? "projeto_id" : "setor_id";

      // 1. Busca todas as atas do projeto
      const { data: atas, error: atasErr } = await supabase
        .from("atas")
        .select("id, nota_id, pauta, local, data_local, texto, proxima_reuniao, redigido_por, criado_em, alterado_em")
        .eq(idField, entity.id)
        .order("criado_em", { ascending: true });

      if (atasErr) throw atasErr;
      if (!atas?.length) { alert("Nenhuma ata encontrada neste projeto."); return; }

      const ataIds    = atas.map(a => a.id);
      const notaIds   = atas.map(a => a.nota_id).filter(Boolean);
      const autorIds  = [...new Set(atas.map(a => a.redigido_por).filter(Boolean))];

      // 2. Busca nomes das notas, autores, participantes e objetivos em paralelo
      const [
        { data: notasNomes },
        { data: autoresPerfis },
        { data: participantesRaw },
        { data: objetivosRaw },
      ] = await Promise.all([
        supabase.from("notas").select("id, nome").in("id", notaIds),
        autorIds.length
          ? supabase.from("profiles").select("id, nome, nickname").in("id", autorIds)
          : Promise.resolve({ data: [] }),
        supabase
          .from("ata_participantes")
          .select("ata_id, nome_externo, funcao_externa, profiles(nome, funcao)")
          .in("ata_id", ataIds),
        supabase
          .from("ata_objetivos")
          .select("id, ata_id, texto, data_entrega, concluido, comentario, ordem")
          .in("ata_id", ataIds)
          .eq("excluido_manualmente", false)
          .order("ordem", { ascending: true }),
      ]);

      // 3. Busca responsáveis dos objetivos pela view enriquecida
      const objIds = (objetivosRaw || []).map(o => o.id);
      let respPorObj = {};
      if (objIds.length) {
        const { data: respRaw } = await supabase
          .from("ata_objetivos_responsaveis_enriquecidos")
          .select("ata_objetivo_id, nome_exibicao, nome_externo")
          .in("ata_objetivo_id", objIds);

        respPorObj = (respRaw || []).reduce((acc, r) => {
          if (!acc[r.ata_objetivo_id]) acc[r.ata_objetivo_id] = [];
          acc[r.ata_objetivo_id].push(r.nome_exibicao || r.nome_externo || "—");
          return acc;
        }, {});
      }

      const notaMap  = Object.fromEntries((notasNomes || []).map(n => [n.id, n.nome]));
      const autorMap = Object.fromEntries((autoresPerfis || []).map(p => [p.id, p.nickname || p.nome || "—"]));

      // Agrupa participantes por ata_id
      const partPorAta = (participantesRaw || []).reduce((acc, p) => {
        if (!acc[p.ata_id]) acc[p.ata_id] = [];
        const nome  = p.profiles?.nome || p.nome_externo || "Participante";
        const funcao = p.profiles?.funcao || p.funcao_externa || "Externo";
        acc[p.ata_id].push({ nome, funcao });
        return acc;
      }, {});

      // Agrupa objetivos por ata_id, injeta responsáveis
      const objPorAta = (objetivosRaw || []).reduce((acc, o) => {
        if (o.texto?.startsWith("[EXCLUIDO]")) return acc;
        if (!acc[o.ata_id]) acc[o.ata_id] = [];
        acc[o.ata_id].push({
          ...o,
          responsaveis: respPorObj[o.id] || [],
        });
        return acc;
      }, {});

      // 3. Monta o ZIP
      const zip = new JSZip();
      const pasta = zip.folder(`atas_${sanitizarNomeArquivo(entity?.name || "projeto")}`);

      atas.forEach((ata, idx) => {
        const nomeNota   = notaMap[ata.nota_id] || `ata_${idx + 1}`;
        const autor      = autorMap[ata.redigido_por] || "—";
        const parts      = partPorAta[ata.id] || [];
        const objs       = objPorAta[ata.id]  || [];
        const dataCriada = ata.criado_em ? formatarData(ata.criado_em) : "—";

        // ── Monta o texto do arquivo ──────────────────────────────────
        const linhas = [];

        linhas.push(`ATA — ${nomeNota}`);
        linhas.push("=".repeat(60));
        linhas.push(`Data/Local  : ${ata.data_local || "—"}`);
        linhas.push(`Local       : ${ata.local      || "—"}`);
        linhas.push(`Pauta       : ${ata.pauta      || "—"}`);
        linhas.push(`Redigida por: ${autor}`);
        linhas.push(`Criada em   : ${dataCriada}`);
        if (ata.proxima_reuniao) linhas.push(`Próx. reunião: ${formatarData(ata.proxima_reuniao)}`);
        linhas.push("");

        // Participantes
        if (parts.length) {
          linhas.push("PARTICIPANTES");
          linhas.push("-".repeat(40));
          parts.forEach(p => linhas.push(`  • ${p.nome} (${p.funcao})`));
          linhas.push("");
        }

        // Texto da ata
        if (ata.texto) {
          linhas.push("TEXTO DA ATA");
          linhas.push("-".repeat(40));
          // Remove tags HTML simples se houver
          const textoLimpo = ata.texto.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
          linhas.push(textoLimpo);
          linhas.push("");
        }

        // Objetivos
        if (objs.length) {
          linhas.push("OBJETIVOS");
          linhas.push("-".repeat(40));
          objs.forEach((o, i) => {
            const status   = o.concluido ? "[✓]" : "[ ]";
            const entrega  = o.data_entrega ? ` | Prazo: ${formatarData(o.data_entrega)}` : "";
            const resps    = o.responsaveis?.length
              ? ` | Resp: ${o.responsaveis.join(", ")}`
              : "";
            linhas.push(`  ${i + 1}. ${status} ${o.texto}${entrega}${resps}`);
            if (o.comentario) linhas.push(`       Comentário: ${o.comentario}`);
          });
          linhas.push("");
        }

        const nomeArquivo = `${String(idx + 1).padStart(3, "0")}_${sanitizarNomeArquivo(nomeNota)}.txt`;
        pasta.file(nomeArquivo, linhas.join("\n"), { binary: false });

        setProgressoAtas(`Processando ata ${idx + 1} de ${atas.length}…`);
      });

      // 4. Gera e baixa o ZIP
      setProgressoAtas("Gerando arquivo ZIP…");
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `atas_${sanitizarNomeArquivo(entity?.name || "projeto")}_${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setProgressoAtas("");
    } catch (err) {
      console.error("Erro ao exportar atas:", err);
      alert(`Erro ao exportar atas: ${err.message || "Erro desconhecido"}`);
      setProgressoAtas("");
    } finally {
      setExportandoAtas(false);
    }
  };

  // ── Export ZIP de todas as listas ─────────────────────────────────────────
  const exportarListasZip = async () => {
    setExportandoListas(true);
    setProgressoListas("Buscando listas…");
    try {
      if (!entity?.id) return;

      const { data: itens, error } = await supabase
        .from("planilha_itens")
        .select("id, nota_id, nota, codigo, descricao, unidade, quantidade, locacao, eap, fornecedor, comentario, observacao, concluido, criado_em, ordem")
        .eq("projeto_id", entity.id)
        .is("item_original_id", null)
        .order("nota_id", { ascending: true })
        .order("ordem",   { ascending: true });

      if (error) throw error;
      if (!itens?.length) { alert("Nenhuma lista encontrada neste projeto."); return; }

      const notaIdsUnicos = [...new Set(itens.map(i => i.nota_id).filter(Boolean))];
      let notaMap = {};
      if (notaIdsUnicos.length) {
        const { data: notasRaw } = await supabase
          .from("notas").select("id, nome").in("id", notaIdsUnicos);
        notaMap = Object.fromEntries((notasRaw || []).map(n => [n.id, n.nome]));
      }

      const grupos = itens.reduce((acc, item) => {
        const chave = item.nota_id || "sem_lista";
        if (!acc[chave]) acc[chave] = [];
        acc[chave].push(item);
        return acc;
      }, {});

      const zip   = new JSZip();
      const pasta = zip.folder(`listas_${sanitizarNomeArquivo(entity?.name || "projeto")}`);
      const listaEntradas = Object.entries(grupos);

      listaEntradas.forEach(([notaId, itensDaLista], idx) => {
        const nomeNota = notaMap[notaId] || itensDaLista[0]?.nota || `lista_${idx + 1}`;
        setProgressoListas(`Processando lista ${idx + 1} de ${listaEntradas.length}…`);

        const cabecalho = ["Código","Descrição","Unidade","Quantidade","Locação","EAP","Fornecedor","Comentário","Observação","Concluído","Criado em"];
        const csvLinhas = [
          cabecalho.join(";"),
          ...itensDaLista.map(i => [
            `"${(i.codigo     || "").replace(/"/g, '""')}"`,
            `"${(i.descricao  || "").replace(/"/g, '""')}"`,
            `"${(i.unidade    || "").replace(/"/g, '""')}"`,
            i.quantidade != null ? String(i.quantidade).replace(".", ",") : "",
            `"${(i.locacao    || "").replace(/"/g, '""')}"`,
            `"${(i.eap        || "").replace(/"/g, '""')}"`,
            `"${(i.fornecedor || "").replace(/"/g, '""')}"`,
            `"${(i.comentario || "").replace(/"/g, '""')}"`,
            `"${(i.observacao || "").replace(/"/g, '""')}"`,
            i.concluido ? "Sim" : "Não",
            formatarData(i.criado_em),
          ].join(";")),
        ];

        pasta.file(
          `${String(idx + 1).padStart(3, "0")}_${sanitizarNomeArquivo(nomeNota)}.csv`,
          "﻿" + csvLinhas.join("\n"),
          { binary: false }
        );
      });

      setProgressoListas("Gerando arquivo ZIP…");
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `listas_${sanitizarNomeArquivo(entity?.name || "projeto")}_${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setProgressoListas("");
    } catch (err) {
      console.error("Erro ao exportar listas:", err);
      alert(`Erro ao exportar listas: ${err.message || "Erro desconhecido"}`);
      setProgressoListas("");
    } finally {
      setExportandoListas(false);
    }
  };

  // ── Export ZIP de todas as tarefas ───────────────────────────────────────
  const exportarTarefasZip = async () => {
    setExportandoTarefas(true);
    setProgressoTarefas("Buscando tarefas…");
    try {
      if (!entity?.id) return;
      const idField = entityType === "project" ? "project_id" : "setor_id";

      // 1. Pilhas do projeto
      const { data: pilhas } = await supabase
        .from("pilhas")
        .select("id, title")
        .eq(idField, entity.id)
        .or("tipo_pilha.is.null,tipo_pilha.neq.sistema_calendario");

      if (!pilhas?.length) { alert("Nenhuma pilha encontrada."); return; }
      const pilhaIds  = pilhas.map(p => p.id);
      const pilhaMap  = Object.fromEntries(pilhas.map(p => [p.id, p.title]));

      // 2. Todas as tarefas
      const { data: tarefas, error: tErr } = await supabase
        .from("notas")
        .select("id, nome, concluida, data_entrega, created_at, responsaveis_ids, pilha_id, descricao, prioridade")
        .in("pilha_id", pilhaIds)
        .eq("tipo", "Tarefas")
        .order("created_at", { ascending: true });

      if (tErr) throw tErr;
      if (!tarefas?.length) { alert("Nenhuma tarefa encontrada neste projeto."); return; }

      const tarefaIds = tarefas.map(t => t.id);

      // 3. Responsáveis, anexos, comentarios e checklist em paralelo
      setProgressoTarefas("Carregando dados…");
      const todosResponsaveisIds = [...new Set(tarefas.flatMap(t => t.responsaveis_ids || []))];

      const [
        { data: perfisResp },
        { data: anexosRaw },
        { data: comentariosRaw },
        { data: checklistsRaw },
        { data: objTarefasRaw },
      ] = await Promise.all([
        todosResponsaveisIds.length
          ? supabase.from("profiles").select("id, nickname, nome").in("id", todosResponsaveisIds)
          : Promise.resolve({ data: [] }),
        supabase.from("anexos").select("id, nota_id, file_name, file_url, mime_type").in("nota_id", tarefaIds),
        // Busca comentários sem join para evitar falha silenciosa de FK
        supabase.from("comentarios").select("id, nota_id, conteudo, created_at, user_id").in("nota_id", tarefaIds).order("created_at", { ascending: true }),
        supabase.from("checklists").select("id, nota_id, title").in("nota_id", tarefaIds),
        // Objetivos de atas vinculados a estas tarefas via task_nota_id
        supabase.from("ata_objetivos").select("id, task_nota_id, texto, concluido, data_entrega").in("task_nota_id", tarefaIds).eq("excluido_manualmente", false),
      ]);

      const perfilMap = Object.fromEntries((perfisResp || []).map(p => [p.id, p.nickname || p.nome || "—"]));

      // Busca perfis dos autores de comentários separadamente
      const comentUserIds = [...new Set((comentariosRaw || []).map(c => c.user_id).filter(Boolean))];
      let comentPerfilMap = {};
      if (comentUserIds.length) {
        const { data: comentPerfis } = await supabase.from("profiles").select("id, nickname, nome").in("id", comentUserIds);
        comentPerfilMap = Object.fromEntries((comentPerfis || []).map(p => [p.id, p.nickname || p.nome || "—"]));
      }

      const anexosPorNota    = (anexosRaw || []).reduce((acc, a) => { if (!acc[a.nota_id]) acc[a.nota_id] = []; acc[a.nota_id].push(a); return acc; }, {});
      const comentsPorNota   = (comentariosRaw || []).reduce((acc, c) => {
        if (!acc[c.nota_id]) acc[c.nota_id] = [];
        acc[c.nota_id].push({ ...c, autorNome: comentPerfilMap[c.user_id] || "—" });
        return acc;
      }, {});
      const checklistPorNota = (checklistsRaw || []).reduce((acc, c) => { acc[c.nota_id] = c; return acc; }, {});
      const objPorTarefa     = (objTarefasRaw || []).reduce((acc, o) => {
        if (!acc[o.task_nota_id]) acc[o.task_nota_id] = [];
        acc[o.task_nota_id].push(o);
        return acc;
      }, {});

      // Itens de checklist
      const checklistIds = (checklistsRaw || []).map(c => c.id);
      let itensPorChecklist = {};
      if (checklistIds.length) {
        const { data: itensRaw } = await supabase
          .from("checklist_items")
          .select("checklist_id, description, is_completed, data_entrega, ordem")
          .in("checklist_id", checklistIds)
          .order("ordem", { ascending: true });
        itensPorChecklist = (itensRaw || []).reduce((acc, i) => {
          if (!acc[i.checklist_id]) acc[i.checklist_id] = [];
          acc[i.checklist_id].push(i);
          return acc;
        }, {});
      }

      // 4. Monta ZIP
      const zip   = new JSZip();
      const pasta = zip.folder(`tarefas_${sanitizarNomeArquivo(entity?.name || "projeto")}`);

      // CSV geral de tarefas
      const csvCabecalho = ["Nome","Pilha","Prioridade","Concluída","Data Entrega","Criada em","Responsáveis","Descrição","Comentários","Anexos","Itens Checklist","Objetivos Vinculados"];
      const csvLinhas = [csvCabecalho.join(";")];

      const totalTarefas = tarefas.length;
      let baixadosComErro = 0;

      for (let idx = 0; idx < totalTarefas; idx++) {
        const t = tarefas[idx];
        setProgressoTarefas(`Processando tarefa ${idx + 1} de ${totalTarefas}…`);

        const resps      = (t.responsaveis_ids || []).map(id => perfilMap[id] || id).join(", ");
        const anexos     = anexosPorNota[t.id]  || [];
        const comments   = comentsPorNota[t.id]  || [];
        const checklist  = checklistPorNota[t.id];
        const itensCheck = checklist ? (itensPorChecklist[checklist.id] || []) : [];

        const objsTarefa = objPorTarefa[t.id] || [];

        const nomeComments = comments.map(c => `[${c.autorNome}]: ${c.conteudo}`).join(" | ");
        const nomeAnexos   = anexos.map(a => a.file_name).join(", ");
        const nomeItens    = itensCheck.map(i => `${i.is_completed ? "✓" : "○"} ${i.description}`).join(" | ");
        const nomeObjs     = objsTarefa.map(o => `${o.concluido ? "[✓]" : "[ ]"} ${o.texto}`).join(" | ");

        csvLinhas.push([
          `"${(t.nome        || "").replace(/"/g, '""')}"`,
          `"${(pilhaMap[t.pilha_id] || "—").replace(/"/g, '""')}"`,
          `"${t.prioridade   || "—"}"`,
          t.concluida ? "Sim" : "Não",
          formatarData(t.data_entrega),
          formatarData(t.created_at),
          `"${resps.replace(/"/g, '""')}"`,
          `"${(t.descricao   || "").replace(/"/g, '""')}"`,
          `"${nomeComments.replace(/"/g, '""')}"`,
          `"${nomeAnexos.replace(/"/g, '""')}"`,
          `"${nomeItens.replace(/"/g, '""')}"`,
          `"${nomeObjs.replace(/"/g, '""')}"`,
        ].join(";"));

        // Pasta individual por tarefa (só se tiver anexos)
        if (anexos.length > 0) {
          const nomePasta = sanitizarNomeArquivo(t.nome || `tarefa_${idx + 1}`);
          const subPasta  = pasta.folder(nomePasta);

          // Texto descritivo da tarefa
          const infoLinhas = [
            `TAREFA: ${t.nome || "—"}`,
            `Pilha: ${pilhaMap[t.pilha_id] || "—"}`,
            `Prioridade: ${t.prioridade || "—"}`,
            `Concluída: ${t.concluida ? "Sim" : "Não"}`,
            `Data de entrega: ${formatarData(t.data_entrega)}`,
            `Responsáveis: ${resps || "—"}`,
            "",
          ];
          if (t.descricao) { infoLinhas.push("DESCRIÇÃO:"); infoLinhas.push(t.descricao.replace(/<[^>]+>/g, "").trim()); infoLinhas.push(""); }
          if (comments.length) {
            infoLinhas.push("COMENTÁRIOS:");
            comments.forEach(c => infoLinhas.push(`  [${c.autorNome}]: ${c.conteudo}`));
            infoLinhas.push("");
          }
          if (objsTarefa.length) {
            infoLinhas.push("OBJETIVOS VINCULADOS (de atas):");
            objsTarefa.forEach(o => {
              const status  = o.concluido ? "[✓]" : "[ ]";
              const entrega = o.data_entrega ? ` | Prazo: ${formatarData(o.data_entrega)}` : "";
              infoLinhas.push(`  ${status} ${o.texto}${entrega}`);
            });
            infoLinhas.push("");
          }
          if (itensCheck.length) {
            infoLinhas.push("CHECKLIST:");
            itensCheck.forEach(i => infoLinhas.push(`  ${i.is_completed ? "[✓]" : "[ ]"} ${i.description}`));
            infoLinhas.push("");
          }
          infoLinhas.push("ANEXOS:");
          anexos.forEach(a => infoLinhas.push(`  • ${a.file_name} — ${a.file_url}`));

          subPasta.file("_info.txt", infoLinhas.join("\n"), { binary: false });

          // Baixa cada anexo
          for (const anexo of anexos) {
            try {
              const resp = await fetch(anexo.file_url);
              if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
              const buffer = await resp.arrayBuffer();
              subPasta.file(anexo.file_name, buffer, { binary: true });
            } catch (e) {
              console.warn(`Falha ao baixar ${anexo.file_name}:`, e);
              baixadosComErro++;
              // Mantém o _info.txt mas pula o arquivo
            }
          }
        }
      }

      pasta.file("dados_tarefas.csv", "\ufeff" + csvLinhas.join("\n"), { binary: false });

      setProgressoTarefas("Gerando arquivo ZIP…");
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `tarefas_${sanitizarNomeArquivo(entity?.name || "projeto")}_${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      if (baixadosComErro > 0) {
        alert(`ZIP gerado! ${baixadosComErro} arquivo(s) não puderam ser baixados e foram omitidos. Os links estão no arquivo _info.txt de cada tarefa.`);
      }
      setProgressoTarefas("");
    } catch (err) {
      console.error("Erro ao exportar tarefas:", err);
      alert(`Erro ao exportar tarefas: ${err.message || "Erro desconhecido"}`);
      setProgressoTarefas("");
    } finally {
      setExportandoTarefas(false);
    }
  };

  // ── Export ZIP de todos os diários de obra ────────────────────────────────
  const exportarDiariosZip = async () => {
    setExportandoDiarios(true);
    setProgressoDiarios("Buscando diários de obra…");
    try {
      if (!entity?.id) return;

      // 1. Busca todos os RDOs do projeto ordenados por data
      const { data: rdos, error: rdoErr } = await supabase
        .from("rdos")
        .select("id, nota_id, data_rdo, dia_semana, projeto_nome, engenheiro_nome, responsavel_preenchimento, clima_manha, clima_tarde, obra_op_manha, obra_op_tarde, efetivo_proprio, efetivo_terceirizado, recebimento_materiais, equipamentos, atividades, intercorrencias, tem_intercorrencias, fotos")
        .eq("project_id", entity.id)
        .order("data_rdo", { ascending: true });

      if (rdoErr) throw rdoErr;
      if (!rdos?.length) { alert("Nenhum diário de obra encontrado neste projeto."); return; }

      // 2. Busca nomes das notas (título do diário)
      const notaIds = rdos.map(r => r.nota_id).filter(Boolean);
      let notaMap = {};
      if (notaIds.length) {
        const { data: notasRaw } = await supabase.from("notas").select("id, nome").in("id", notaIds);
        notaMap = Object.fromEntries((notasRaw || []).map(n => [n.id, n.nome]));
      }

      const zip   = new JSZip();
      const pasta = zip.folder(`diarios_${sanitizarNomeArquivo(entity?.name || "projeto")}`);
      const total = rdos.length;
      let errosDownload = 0;

      for (let idx = 0; idx < total; idx++) {
        const rdo = rdos[idx];
        setProgressoDiarios(`Processando diário ${idx + 1} de ${total}…`);

        const nomeNota   = notaMap[rdo.nota_id] || `diario_${idx + 1}`;
        const dataFmt    = rdo.data_rdo
          ? rdo.data_rdo.split("-").reverse().join("/")
          : "sem_data";
        const nomePasta  = `${String(idx + 1).padStart(3, "0")}_${sanitizarNomeArquivo(nomeNota)}`;
        const subPasta   = pasta.folder(nomePasta);

        // ── Monta _info.txt ────────────────────────────────────────────
        const linhas = [];
        linhas.push(`DIÁRIO DE OBRA — ${nomeNota}`);
        linhas.push("=".repeat(60));
        linhas.push(`Data        : ${dataFmt}${rdo.dia_semana ? ` (${rdo.dia_semana})` : ""}`);
        linhas.push(`Projeto     : ${rdo.projeto_nome || "—"}`);
        linhas.push(`Engenheiro  : ${rdo.engenheiro_nome || "—"}`);
        linhas.push(`Responsável : ${rdo.responsavel_preenchimento || "—"}`);
        linhas.push("");

        // Clima
        const climaLabel = { seco: "Ensolarado", chuvoso: "Chuvoso" };
        const opLabel    = { sim: "Sim", nao: "Não" };
        linhas.push("CLIMA");
        linhas.push("-".repeat(40));
        linhas.push(`  Manhã : ${climaLabel[rdo.clima_manha] || "—"} | Obra operacional: ${opLabel[rdo.obra_op_manha] || "—"}`);
        linhas.push(`  Tarde : ${climaLabel[rdo.clima_tarde] || "—"} | Obra operacional: ${opLabel[rdo.obra_op_tarde] || "—"}`);
        linhas.push("");

        // Atividades por pavimento
        if (rdo.atividades && Object.keys(rdo.atividades).length) {
          linhas.push("ATIVIDADES EXECUTADAS");
          linhas.push("-".repeat(40));
          Object.entries(rdo.atividades).forEach(([pav, desc]) => {
            if (desc?.trim()) {
              linhas.push(`  [${pav}]`);
              linhas.push(`  ${desc.trim()}`);
              linhas.push("");
            }
          });
        }

        // Efetivo próprio
        const efProp = Array.isArray(rdo.efetivo_proprio) ? rdo.efetivo_proprio.filter(e => e.funcao || e.total) : [];
        if (efProp.length) {
          linhas.push("EFETIVO PRÓPRIO");
          linhas.push("-".repeat(40));
          efProp.forEach(e => linhas.push(`  ${e.funcao || "—"} | Total: ${e.total || 0} | Presentes: ${e.presentes || 0}`));
          linhas.push("");
        }

        // Efetivo terceirizado
        const efTerc = Array.isArray(rdo.efetivo_terceirizado) ? rdo.efetivo_terceirizado.filter(e => e.funcao || e.total) : [];
        if (efTerc.length) {
          linhas.push("EFETIVO TERCEIRIZADO");
          linhas.push("-".repeat(40));
          efTerc.forEach(e => linhas.push(`  ${e.funcao || "—"} | Total: ${e.total || 0} | Presentes: ${e.presentes || 0}`));
          linhas.push("");
        }

        // Recebimento de materiais
        const mats = Array.isArray(rdo.recebimento_materiais) ? rdo.recebimento_materiais.filter(m => m.descricao || m.codigo) : [];
        if (mats.length) {
          linhas.push("RECEBIMENTO DE MATERIAIS");
          linhas.push("-".repeat(40));
          mats.forEach(m => linhas.push(`  [${m.codigo || "—"}] ${m.descricao || "—"} | Un: ${m.unidade || "—"} | Qtd: ${m.quantidade || 0} | Empresa: ${m.empresa || "—"}`));
          linhas.push("");
        }

        // Equipamentos
        const equips = Array.isArray(rdo.equipamentos) ? rdo.equipamentos.filter(e => e.descricao || e.codigo) : [];
        if (equips.length) {
          linhas.push("EQUIPAMENTOS");
          linhas.push("-".repeat(40));
          equips.forEach(e => linhas.push(`  [${e.codigo || "—"}] ${e.descricao || "—"} | Total: ${e.total || 0} | Em uso: ${e.em_uso || 0}`));
          linhas.push("");
        }

        // Intercorrências
        if (rdo.tem_intercorrencias && rdo.intercorrencias?.trim()) {
          linhas.push("INTERCORRÊNCIAS");
          linhas.push("-".repeat(40));
          linhas.push(`  ${rdo.intercorrencias.trim()}`);
          linhas.push("");
        }

        // Fotos — lista de URLs
        const fotos = Array.isArray(rdo.fotos) ? rdo.fotos.filter(Boolean) : [];
        if (fotos.length) {
          linhas.push("FOTOS");
          linhas.push("-".repeat(40));
          fotos.forEach((url, fi) => linhas.push(`  Foto ${fi + 1}: ${url}`));
          linhas.push("");
        }

        subPasta.file("_info.txt", linhas.join("\n"), { binary: false });

        // ── Baixa as fotos ─────────────────────────────────────────────
        if (fotos.length) {
          setProgressoDiarios(`Baixando fotos do diário ${idx + 1} de ${total}…`);
          for (let fi = 0; fi < fotos.length; fi++) {
            const url = fotos[fi];
            try {
              const resp = await fetch(url);
              if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
              const buffer = await resp.arrayBuffer();
              // Extrai extensão da URL ou usa jpg como fallback
              const ext = url.split("?")[0].split(".").pop().toLowerCase() || "jpg";
              const nomeFoto = `foto_${String(fi + 1).padStart(2, "0")}.${ext}`;
              subPasta.file(nomeFoto, buffer, { binary: true });
            } catch (e) {
              console.warn(`Falha ao baixar foto ${fi + 1} do diário ${idx + 1}:`, e);
              errosDownload++;
            }
          }
        }
      }

      setProgressoDiarios("Gerando arquivo ZIP…");
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `diarios_${sanitizarNomeArquivo(entity?.name || "projeto")}_${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      if (errosDownload > 0) {
        alert(`ZIP gerado! ${errosDownload} foto(s) não puderam ser baixadas e foram omitidas. Os links estão no arquivo _info.txt de cada diário.`);
      }
      setProgressoDiarios("");
    } catch (err) {
      console.error("Erro ao exportar diários:", err);
      alert(`Erro ao exportar diários: ${err.message || "Erro desconhecido"}`);
      setProgressoDiarios("");
    } finally {
      setExportandoDiarios(false);
    }
  };

  return (
    <div className="cfg-root">

      <div className="cfg-sections">

        {/* ── SEÇÃO 1: Lista de notas ────────────────────────────────── */}
        <section className="cfg-section">
          <div className="cfg-section__head">
            <span className="material-symbols-outlined">list_alt</span>
            <div>
              <h3 className="cfg-section__title">Exportar lista de notas</h3>
              <p className="cfg-section__desc">Todas as notas do projeto, incluindo arquivadas, ordenadas por data de criação.</p>
            </div>
            <button
              className="cfg-btn cfg-btn--primary"
              onClick={exportarListaCSV}
              disabled={exportando || loading || notasFiltradas.length === 0}
            >
              <span className="material-symbols-outlined">download</span>
              {exportando ? "Exportando…" : "Exportar CSV"}
            </button>
          </div>

          {loading ? (
            <div className="cfg-loading">
              <span className="material-symbols-outlined cfg-spin">progress_activity</span>
              Carregando notas…
            </div>
          ) : notas.length === 0 ? (
            <p className="cfg-empty">Nenhuma nota encontrada neste projeto.</p>
          ) : (
            <>
              {/* Filtros */}
              <div className="cfg-resumo">
                <button
                  className={`cfg-resumo-card${!filtroTipo ? " cfg-resumo-card--active" : ""}`}
                  onClick={() => setFiltroTipo(null)}
                  title="Mostrar todas"
                >
                  <span className="material-symbols-outlined cfg-resumo-icon">inventory_2</span>
                  <span className="cfg-resumo-num">{notas.length}</span>
                  <span className="cfg-resumo-label">Todas</span>
                </button>
                {tiposPresentes.map(t => (
                  <button
                    key={t}
                    className={`cfg-resumo-card cfg-resumo-card--${t.toLowerCase().replace(/ /g, "-")}${filtroTipo === t ? " cfg-resumo-card--active" : ""}`}
                    onClick={() => setFiltroTipo(prev => prev === t ? null : t)}
                    title={`Filtrar por ${TIPO_LABEL[t] || t}`}
                  >
                    <span className={`material-symbols-outlined cfg-resumo-icon cfg-resumo-icon--${t.toLowerCase().replace(/ /g, "-")}`}>
                      {TIPO_ICON[t] || "sticky_note_2"}
                    </span>
                    <span className="cfg-resumo-num">{resumo[t]}</span>
                    <span className="cfg-resumo-label">{TIPO_LABEL[t] || t}</span>
                  </button>
                ))}
              </div>

              {/* Tabela */}
              <div className="cfg-table-wrap">
                <table className="cfg-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>Pilha</th>
                      <th>Criado em</th>
                      <th>Criado por</th>
                      <th title="Comentários"><span className="material-symbols-outlined" style={{ fontSize: 14 }}>chat_bubble</span></th>
                      <th title="Anexos"><span className="material-symbols-outlined" style={{ fontSize: 14 }}>attach_file</span></th>
                      <th title="Imagens"><span className="material-symbols-outlined" style={{ fontSize: 14 }}>image</span></th>
                      <th title="Objetivos"><span className="material-symbols-outlined" style={{ fontSize: 14 }}>checklist</span></th>
                    </tr>
                  </thead>
                </table>
                <div className="cfg-table-body">
                  <table className="cfg-table">
                    <tbody>
                      {notasFiltradas.map(n => (
                        <tr key={n.id} className={n.pilhaArquivada ? "cfg-table__row--archived" : ""}>
                          <td className="cfg-table__nome">
                            {n.nome}
                            {n.pilhaArquivada && <span className="cfg-archived-tag">arquivada</span>}
                          </td>
                          <td><span className={TIPO_CLASS[n.tipo] || "cfg-badge cfg-badge--default"}>{n.tipo}</span></td>
                          <td className="cfg-table__pilha">{n.pilhaTitulo}</td>
                          <td className="cfg-table__date">{formatarData(n.created_at)}</td>
                          <td className="cfg-table__user">{n.criadoPor}</td>
                          <td className="cfg-table__meta">{n.meta.comentarios > 0 ? <span className="cfg-meta-pill cfg-meta-pill--coment">{n.meta.comentarios}</span> : <span className="cfg-meta-dash">—</span>}</td>
                          <td className="cfg-table__meta">{n.meta.anexos > 0 ? <span className="cfg-meta-pill cfg-meta-pill--anexo">{n.meta.anexos}</span> : <span className="cfg-meta-dash">—</span>}</td>
                          <td className="cfg-table__meta">{n.meta.imagens > 0 ? <span className="cfg-meta-pill cfg-meta-pill--img">{n.meta.imagens}</span> : <span className="cfg-meta-dash">—</span>}</td>
                          <td className="cfg-table__meta">{n.meta.objetivos > 0 ? <span className="cfg-meta-pill cfg-meta-pill--obj">{n.meta.objetivos}</span> : <span className="cfg-meta-dash">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="cfg-count">
                  {notasFiltradas.length} nota{notasFiltradas.length !== 1 ? "s" : ""}
                  {filtroTipo ? ` · filtrado por ${TIPO_LABEL[filtroTipo] || filtroTipo}` : " no total"}
                </p>
              </div>
            </>
          )}
        </section>

        {/* ── SEÇÃO 2: Exportar atas (ativo) ───────────────────────── */}
        <section className="cfg-section">
          <div className="cfg-section__head">
            <span className="material-symbols-outlined">description</span>
            <div>
              <h3 className="cfg-section__title">Exportar todas as atas</h3>
              <p className="cfg-section__desc">
                Gera um arquivo <strong>.zip</strong> com uma pasta contendo um arquivo <code>.txt</code> por ata —
                incluindo pauta, participantes, texto e objetivos.
                {resumo["Atas"] ? ` ${resumo["Atas"]} ata${resumo["Atas"] !== 1 ? "s" : ""} encontrada${resumo["Atas"] !== 1 ? "s" : ""}.` : ""}
              </p>
            </div>
            <button
              className="cfg-btn cfg-btn--primary"
              onClick={exportarAtasZip}
              disabled={exportandoAtas || loading || !resumo["Atas"]}
            >
              <span className="material-symbols-outlined">folder_zip</span>
              {exportandoAtas ? (progressoAtas || "Exportando…") : "Exportar ZIP"}
            </button>
          </div>
          {exportandoAtas && progressoAtas && (
            <div className="cfg-loading" style={{ marginTop: 12 }}>
              <span className="material-symbols-outlined cfg-spin">progress_activity</span>
              {progressoAtas}
            </div>
          )}
        </section>

        {/* ── SEÇÃO 3: Exportar listas (ativo) ────────────────────── */}
        <section className="cfg-section">
          <div className="cfg-section__head">
            <span className="material-symbols-outlined">checklist</span>
            <div>
              <h3 className="cfg-section__title">Exportar todas as listas</h3>
              <p className="cfg-section__desc">
                Gera um arquivo <strong>.zip</strong> com um <code>.csv</code> por lista de materiais,
                contendo todos os itens com código, descrição, quantidade, EAP, fornecedor e status.
                {resumo["Lista"] ? ` ${resumo["Lista"]} lista${resumo["Lista"] !== 1 ? "s" : ""} encontrada${resumo["Lista"] !== 1 ? "s" : ""}.` : ""}
              </p>
            </div>
            <button
              className="cfg-btn cfg-btn--primary"
              onClick={exportarListasZip}
              disabled={exportandoListas || loading || !resumo["Lista"]}
            >
              <span className="material-symbols-outlined">folder_zip</span>
              {exportandoListas ? (progressoListas || "Exportando…") : "Exportar ZIP"}
            </button>
          </div>
          {exportandoListas && progressoListas && (
            <div className="cfg-loading" style={{ marginTop: 12 }}>
              <span className="material-symbols-outlined cfg-spin">progress_activity</span>
              {progressoListas}
            </div>
          )}
        </section>

        {/* ── SEÇÃO 4: Exportar tarefas (ativo) ───────────────────── */}
        <section className="cfg-section">
          <div className="cfg-section__head">
            <span className="material-symbols-outlined">task_alt</span>
            <div>
              <h3 className="cfg-section__title">Exportar todas as tarefas</h3>
              <p className="cfg-section__desc">
                Gera um <strong>.zip</strong> com um <code>dados_tarefas.csv</code> completo e,
                para cada tarefa com anexos, uma pasta individual contendo os arquivos baixados
                e um <code>_info.txt</code> com descrição, comentários e checklist.
                {resumo["Tarefas"] ? ` ${resumo["Tarefas"]} tarefa${resumo["Tarefas"] !== 1 ? "s" : ""} encontrada${resumo["Tarefas"] !== 1 ? "s" : ""}.` : ""}
              </p>
            </div>
            <button
              className="cfg-btn cfg-btn--primary"
              onClick={exportarTarefasZip}
              disabled={exportandoTarefas || loading || !resumo["Tarefas"]}
            >
              <span className="material-symbols-outlined">folder_zip</span>
              {exportandoTarefas ? (progressoTarefas || "Exportando…") : "Exportar ZIP"}
            </button>
          </div>
          {exportandoTarefas && progressoTarefas && (
            <div className="cfg-loading" style={{ marginTop: 12 }}>
              <span className="material-symbols-outlined cfg-spin">progress_activity</span>
              {progressoTarefas}
            </div>
          )}
        </section>

        {/* ── SEÇÃO 5: Exportar diários de obra (ativo) ──────────── */}
        <section className="cfg-section">
          <div className="cfg-section__head">
            <span className="material-symbols-outlined">construction</span>
            <div>
              <h3 className="cfg-section__title">Exportar todos os diários de obra</h3>
              <p className="cfg-section__desc">
                Gera um <strong>.zip</strong> com uma pasta por diário contendo um <code>_info.txt</code>
                com clima, atividades por pavimento, efetivos, materiais, equipamentos e intercorrências,
                além das <strong>fotos baixadas</strong> diretamente.
                {resumo["Diário de Obra"] ? ` ${resumo["Diário de Obra"]} diário${resumo["Diário de Obra"] !== 1 ? "s" : ""} encontrado${resumo["Diário de Obra"] !== 1 ? "s" : ""}.` : ""}
              </p>
            </div>
            <button
              className="cfg-btn cfg-btn--primary"
              onClick={exportarDiariosZip}
              disabled={exportandoDiarios || loading || !resumo["Diário de Obra"]}
            >
              <span className="material-symbols-outlined">folder_zip</span>
              {exportandoDiarios ? (progressoDiarios || "Exportando…") : "Exportar ZIP"}
            </button>
          </div>
          {exportandoDiarios && progressoDiarios && (
            <div className="cfg-loading" style={{ marginTop: 12 }}>
              <span className="material-symbols-outlined cfg-spin">progress_activity</span>
              {progressoDiarios}
            </div>
          )}
        </section>

        {/* ── SEÇÃO 6: Inativar projeto ─────────────────────────────── */}
        <section className="cfg-section">
          <div className="cfg-section__head">
            <span className="material-symbols-outlined">{ativo ? "visibility_off" : "visibility"}</span>
            <div>
              <h3 className="cfg-section__title">{ativo ? "Inativar projeto" : "Reativar projeto"}</h3>
              <p className="cfg-section__desc">
                {ativo
                  ? "O projeto ficará com filtro cinza e badge \"Inativo\" no container, mas continuará acessível."
                  : "O projeto voltará a aparecer normalmente no container."}
              </p>
            </div>
            <button
              className={`cfg-btn ${ativo ? "cfg-btn--warning" : "cfg-btn--primary"}`}
              onClick={toggleAtivo}
              disabled={alterandoStatus}
            >
              <span className="material-symbols-outlined">{ativo ? "pause_circle" : "play_circle"}</span>
              {alterandoStatus ? "Alterando…" : ativo ? "Inativar" : "Reativar"}
            </button>
          </div>
        </section>

        {/* ── SEÇÃO 7: Apagar projeto (danger zone) ───────────────────── */}
        <section className="cfg-section cfg-section--danger">
          <div className="cfg-section__head">
            <span className="material-symbols-outlined">delete_forever</span>
            <div>
              <h3 className="cfg-section__title">Apagar projeto</h3>
              <p className="cfg-section__desc">
                Ação irreversível. O projeto e todos os seus dados (atas, tarefas, listas, diários) serão removidos permanentemente.
              </p>
            </div>
            <button
              className="cfg-btn cfg-btn--danger"
              onClick={() => setShowDeleteModal(true)}
            >
              <span className="material-symbols-outlined">delete</span>
              Apagar projeto
            </button>
          </div>
        </section>

      </div>

      {/* ── Modal de deleção ──────────────────────────────────────────────── */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="cfg-delete-modal">
            <h3>Apagar "{entity?.name}"</h3>

            {!deleteOption ? (
              <>
                <p>Escolha como deseja proceder:</p>
                <div className="cfg-delete-opcoes">
                  <button className="cfg-delete-opcao" onClick={() => setDeleteOption("download")}>
                    <span className="material-symbols-outlined">download</span>
                    <div>
                      <strong>Baixar tudo e apagar</strong>
                      <p>Exporta atas, listas, tarefas e diários antes de apagar.</p>
                    </div>
                  </button>
                  <button className="cfg-delete-opcao cfg-delete-opcao--danger" onClick={() => setDeleteOption("direto")}>
                    <span className="material-symbols-outlined">delete_forever</span>
                    <div>
                      <strong>Apagar direto</strong>
                      <p>Remove o projeto sem exportar nada. Não pode ser desfeito.</p>
                    </div>
                  </button>
                </div>
                <button className="cfg-btn cfg-btn--cancelar" onClick={() => setShowDeleteModal(false)}>
                  Cancelar
                </button>
              </>
            ) : (
              <>
                <p>
                  {deleteOption === "download"
                    ? "Os arquivos serão baixados e, em seguida, o projeto será apagado permanentemente."
                    : "O projeto será apagado permanentemente, sem exportação."}
                </p>
                <p>Digite <strong>{entity?.name}</strong> para confirmar:</p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={entity?.name}
                  className="cfg-delete-input"
                />
                <div className="cfg-delete-modal__footer">
                  <button
                    className="cfg-btn cfg-btn--cancelar"
                    onClick={() => { setDeleteOption(null); setConfirmText(""); }}
                    disabled={deletando}
                  >
                    Voltar
                  </button>
                  <button
                    className="cfg-btn cfg-btn--danger"
                    onClick={confirmarDelecao}
                    disabled={deletando || confirmText !== entity?.name}
                  >
                    {deletando ? "Apagando…" : "Confirmar e apagar"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}